import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useAccounts } from "@/api/accounts";
import {
  FIELD_LABELS,
  OUTCOME_LABELS,
  commitImport,
  previewImport,
  useImportAction,
  useImports,
  useStarterProfiles,
  useDeleteImportProfile,
  useImportProfiles,
  useSaveImportProfile,
  type ImportBatchDetail,
  type Preview,
  type ProfileSettings,
} from "@/api/imports";
import { Amount } from "@/components/Amount";
import {
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  PageTitle,
  Panel,
  Section,
  Tag,
  buttonClass,
  inputClass,
  quietButtonClass,
  rowsClass,
  secondaryButtonClass,
} from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";

/**
 * The column-mapping screen (`model.md` §3.10, K-29).
 *
 * **Nothing here writes a transaction.** `project.md` §3 makes reversibility
 * non-negotiable for imports, and the cheapest form of it is not having done it
 * yet: every change on this screen re-reads the file and shows what *would*
 * land. K-31 is the story that commits it.
 *
 * Two rules from `project.md` §5 shape the flow.
 *
 * **Suggest, don't demand** (§5.4). Dropping a file in produces a column list
 * with a mapping already proposed from the column names — "Buchungstag" is a
 * booking date at every German bank — and the pickers start there rather than
 * empty. Nothing is saved until somebody says so.
 *
 * **Show the failures next to the data** (§3's reversibility, one step earlier).
 * A row that will not parse appears in the table with its reason, because a
 * count of failures tells nobody which cell is wrong.
 *
 * This really is a sequence — a file becomes a mapping becomes a preview
 * becomes a commit — so it is drawn as one, a numbered `Panel` per step.
 */
export function ImportPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();

  const accounts = useAccounts(householdId);
  const profiles = useImportProfiles(householdId);
  const save = useSaveImportProfile(householdId);
  const remove = useDeleteImportProfile(householdId);

  const [file, setFile] = useState<File>();
  const [profileId, setProfileId] = useState("");
  const [settings, setSettings] = useState<ProfileSettings>({});
  const [preview, setPreview] = useState<Preview>();
  const [previewError, setPreviewError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [imported, setImported] = useState<ImportBatchDetail>();
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<unknown>();

  const imports = useImports(householdId);
  const importAction = useImportAction(householdId);
  const starters = useStarterProfiles(householdId);
  const client = useQueryClient();

  const mapping = settings.field_mapping ?? {};

  // The account the rows land in: what the picker says, or — when a saved
  // profile was chosen — the default that profile carries. A profile that knows
  // its account should be enough to import with, without the household picking
  // the same account again.
  const accountId = settings.default_account_id ?? preview?.account_id ?? "";

  // Every change re-reads the file. That is affordable because the file is
  // already in the browser and the server writes nothing — and it is what makes
  // a mapping something you *see* rather than something you reason about.
  useEffect(() => {
    if (!file) return;

    let cancelled = false;
    setBusy(true);
    setPreviewError(undefined);

    // Debounced, because the settings above include a number field and a
    // statement can be several megabytes: without this every keystroke uploads
    // the file again.
    const timer = setTimeout(() => {
      previewImport(householdId, file, {
        ...(profileId ? { profileId } : {}),
        ...(settings.default_account_id
          ? { accountId: settings.default_account_id }
          : {}),
        settings,
      })
        .then((got) => {
          if (cancelled) return;
          setPreview(got);
          // The suggestion fills the pickers only when nothing has been chosen
          // yet, so re-previewing never overwrites a decision somebody made.
          setSettings((current) =>
            current.field_mapping
              ? current
              : {
                  ...current,
                  field_mapping: got.suggested_mapping,
                  amount_mode: got.suggested_amount_mode,
                },
          );
        })
        .catch((err) => {
          if (!cancelled) setPreviewError(err);
        })
        .finally(() => {
          if (!cancelled) setBusy(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [file, householdId, profileId, settings]);

  function onFile(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    setFile(chosen);
    setPreview(undefined);
    setImported(undefined);
    setSkipped(new Set());
    // A new file is a new mapping question. Keeping the old one would map this
    // bank's columns onto the last bank's fields.
    setSettings({});
    setName(chosen.name.replace(/\.[^.]+$/, ""));
  }

  function setField(column: string, field: string) {
    setSettings((current) => {
      const next = { ...(current.field_mapping ?? {}) };
      if (field === "") {
        delete next[column];
      } else {
        next[column] = field;
      }
      return { ...current, field_mapping: next };
    });
  }

  function onSelectProfile(id: string) {
    setProfileId(id);
    // The saved profile's settings replace whatever is on screen, which is what
    // picking one means.
    setSettings({});
  }

  const violation = violationsOf(t, save.error);

  const rows = preview?.rows ?? [];
  const saved = profiles.data?.items ?? [];

  return (
    <div>
      <PageTitle lead={t("import.lead")}>{t("nav.import")}</PageTitle>

      <Step n={1} title={t("import.step1Title")}>
        {saved.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="import-profile" className="sr-only">
              {t("import.savedProfile")}
            </label>
            <select
              id="import-profile"
              value={profileId}
              onChange={(e) => onSelectProfile(e.target.value)}
              className={`${inputClass} mt-0 w-auto min-h-11 text-base sm:text-sm`}
            >
              <option value="">{t("import.newProfile")}</option>
              {saved.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.times_used > 0 && ` (${p.times_used}×)`}
                </option>
              ))}
            </select>
            {profileId && (
              <button
                type="button"
                onClick={() => {
                  remove.mutate(profileId);
                  setProfileId("");
                }}
                className={`${quietButtonClass} min-h-11`}
              >
                {t("import.deleteProfile")}
              </button>
            )}
          </div>
        )}

        <div className={saved.length > 0 ? "mt-4" : ""}>
          <label htmlFor="import-file" className="block text-sm font-medium text-ink">
            {t("import.statementFile")}
          </label>
          <input
            id="import-file"
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={onFile}
            className="mt-1.5 block w-full text-sm text-ink file:mr-3 file:rounded-control file:border-0 file:bg-accent file:px-4 file:py-2 file:text-sm file:font-medium file:text-accent-ink hover:file:bg-accent/90"
          />
        </div>

        {file && (starters.data?.items.length ?? 0) > 0 && (
          <div className="mt-4">
            <label htmlFor="import-bank" className="block text-sm font-medium text-ink">
              {t("account.bank")}
            </label>
            <select
              id="import-bank"
              defaultValue=""
              onChange={(e) => {
                const starter = starters.data?.items.find(
                  (s) => s.key === e.target.value,
                );
                if (!starter) return;
                // The bank's settings replace what is on screen, except the
                // account: that is the household's choice and has nothing to do
                // with the file's format.
                setSettings((current) => ({
                  ...starter.settings,
                  ...(current.default_account_id
                    ? { default_account_id: current.default_account_id }
                    : {}),
                }));
                // The profile's own name is the household's to choose; the bank's
                // is only a suggestion for the field.
                setName((current) => current || starter.bank);
                setProfileId("");
              }}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
              data-testid="starter-picker"
            >
              <option value="">{`${t("import.chooseBank")} (${t("common.optional")})`}</option>
              {(starters.data?.items ?? []).map((starter) => (
                <option key={starter.key} value={starter.key}>
                  {starter.bank}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-muted" data-testid="starter-note">
              {starters.data?.items.find(
                (s) =>
                  s.settings.delimiter === settings.delimiter &&
                  settings.delimiter,
              )?.note ??
                t("import.bankHint")}
            </p>
          </div>
        )}
      </Step>

      <ErrorNote error={previewError} />
      <ErrorNote error={profiles.error} />
      {busy && <Loading />}

      {!file && !busy && (
        <EmptyState title={t("import.noFileTitle")}>{t("import.noFileBody")}</EmptyState>
      )}

      {preview && (
        <>
          <Step n={2} title={t("import.mapColumns")}>
            <p className="text-xs text-muted" data-testid="detected">
              {t("import.detectedColumns", { n: preview.columns.length })} ·{" "}
              {t("import.detectedDelimiter")}{" "}
              <code className="rounded bg-sunken px-1 py-0.5">
                {preview.delimiter === "\t" ? "Tab" : preview.delimiter}
              </code>{" "}
              · {preview.encoding} ·{" "}
              {t("import.detectedRows", { n: preview.rows_total })} ·{" "}
              {t(
                preview.header_row_count === 1
                  ? "import.detectedHeaderRow"
                  : "import.detectedHeaderRows",
                { n: preview.header_row_count },
              )}
            </p>

            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {preview.columns.map((column) => (
                <li key={column} data-testid="column-mapping">
                  <label
                    htmlFor={`map-${column}`}
                    className="block truncate text-xs text-muted"
                  >
                    {column}
                  </label>
                  <select
                    id={`map-${column}`}
                    value={mapping[column] ?? ""}
                    onChange={(e) => setField(column, e.target.value)}
                    className={`${inputClass} mt-0.5 min-h-11 text-base sm:text-sm`}
                  >
                    <option value="">{t("import.doNotImport")}</option>
                    {preview.mappable_fields.map((field) => (
                      <option key={field} value={field}>
                        {FIELD_LABELS[field] ? t(FIELD_LABELS[field]) : field}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <Field label={t("import.headerRows")} htmlFor="import-header-rows">
                <input
                  id="import-header-rows"
                  type="number"
                  min={0}
                  value={settings.header_row_count ?? preview.header_row_count}
                  onChange={(e) =>
                    setSettings((c) => ({
                      ...c,
                      header_row_count: Number(e.target.value),
                    }))
                  }
                  className={`${inputClass} min-h-11 text-base sm:text-sm`}
                />
              </Field>

              <Field label={t("import.thousandsSeparator")} htmlFor="import-thousands">
                <select
                  id="import-thousands"
                  value={settings.thousands_separator ?? ""}
                  onChange={(e) =>
                    setSettings((c) => ({
                      ...c,
                      thousands_separator: e.target.value,
                    }))
                  }
                  className={`${inputClass} min-h-11 text-base sm:text-sm`}
                >
                  <option value="">{t("import.separatorNone")}</option>
                  <option value=".">{t("import.separatorDot")}</option>
                  <option value=",">{t("import.separatorComma")}</option>
                  <option value=" ">{t("import.separatorSpace")}</option>
                </select>
              </Field>

              <Field label={t("import.encoding")} htmlFor="import-encoding">
                <select
                  id="import-encoding"
                  value={settings.encoding ?? ""}
                  onChange={(e) =>
                    setSettings((c) => ({ ...c, encoding: e.target.value }))
                  }
                  className={`${inputClass} min-h-11 text-base sm:text-sm`}
                >
                  <option value="">
                    {t("import.encodingAuto", { encoding: preview.encoding })}
                  </option>
                  <option value="utf-8">UTF-8</option>
                  <option value="ISO-8859-1">ISO-8859-1</option>
                  <option value="ISO-8859-15">ISO-8859-15</option>
                </select>
              </Field>
            </div>
          </Step>

          <Step n={3} title={t("import.preview")}>
            <p className="text-xs text-muted" data-testid="preview-summary">
              {t("import.rowsOf", { shown: rows.length, total: preview.rows_total })}
              {preview.rows_failed > 0 &&
                ` · ${t("import.rowsFailed", { n: preview.rows_failed })}`}
              {preview.rows_duplicate > 0 &&
                ` · ${t("import.rowsDuplicate", { n: preview.rows_duplicate })}`}
            </p>

            {rows.length === 0 ? (
              <p className="mt-3 text-sm text-muted">{t("import.mapAtLeast")}</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs font-medium text-muted">
                    <tr>
                      <th className="py-1 pr-3 font-medium">{t("import.row")}</th>
                      <th className="py-1 pr-3 font-medium">{t("transactions.date")}</th>
                      <th className="py-1 pr-3 font-medium">{t("transactions.payee")}</th>
                      <th className="py-1 pr-3 text-right font-medium">
                        {t("transactions.amount")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {rows.map((row) => {
                      const hasDuplicate = Boolean(row.duplicates && row.duplicates.length > 0);
                      return (
                        <tr
                          key={row.number}
                          data-testid="preview-row"
                          className={hasDuplicate ? "bg-sunken" : ""}
                        >
                          <td className="py-1.5 pr-3 text-xs text-muted">
                            <label className="flex items-center gap-1.5">
                              {/*
                                A checkbox per line, because K-30's duplicate flag
                                is a question and this is where it gets answered.
                                A line that will not parse cannot be imported at
                                all, so its box is not offered.
                              */}
                              {!row.error && (
                                <input
                                  type="checkbox"
                                  checked={!skipped.has(row.number)}
                                  onChange={(e) =>
                                    setSkipped((current) => {
                                      const next = new Set(current);
                                      if (e.target.checked) {
                                        next.delete(row.number);
                                      } else {
                                        next.add(row.number);
                                      }
                                      return next;
                                    })
                                  }
                                  aria-label={t("import.includeRow", { n: row.number })}
                                  data-testid="row-include"
                                />
                              )}
                              {row.number}
                            </label>
                          </td>
                          {row.error ? (
                            // The failure sits where the data would have been, so
                            // it is impossible to read the table and miss it. This
                            // is a genuine error, so — unlike the duplicate flag
                            // below — the red token belongs here.
                            <td colSpan={3} className="py-1.5 text-xs text-overspend">
                              {row.error}
                            </td>
                          ) : (
                            <>
                              <td className="py-1.5 pr-3 text-ink">
                                {format.date(row.booked_date ?? "")}
                              </td>
                              <td className="py-1.5 pr-3 truncate text-ink">
                                {row.payee}
                                {hasDuplicate && (
                                  // A flag, never a rejection: the row is still
                                  // there to import and the household decides.
                                  // Not red — red is overspend (project.md §5.5),
                                  // and this is a question rather than an alarm.
                                  <span
                                    className="ml-2 inline-flex align-middle"
                                    data-testid="duplicate-flag"
                                    title={
                                      row.duplicates?.[0]?.strength === "bank_reference"
                                        ? t("import.duplicateBankTitle")
                                        : t("import.duplicateSoftTitle")
                                    }
                                  >
                                    <Tag>
                                      {row.duplicates?.[0]?.strength === "bank_reference"
                                        ? t("import.duplicateBank")
                                        : t("import.duplicateSoft")}
                                    </Tag>
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 text-right">
                                <Amount
                                  minor={row.amount_minor}
                                  currency={row.currency_code || "EUR"}
                                  testId="preview-amount"
                                />
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Step>

          <Step n={4} title={t("import.commit")}>
            {!accountId ? (
              <p className="text-sm text-muted">{t("import.chooseAnAccount")}</p>
            ) : (
              <>
                {preview.rows_duplicate > 0 && (
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={skipDuplicates}
                      onChange={(e) => setSkipDuplicates(e.target.checked)}
                      data-testid="skip-duplicates"
                    />
                    {t(
                      preview.rows_duplicate === 1
                        ? "import.skipDuplicateOne"
                        : "import.skipDuplicates",
                      { n: preview.rows_duplicate },
                    )}
                  </label>
                )}

                <button
                  type="button"
                  disabled={importing || Boolean(imported)}
                  onClick={() => {
                    setImportError(undefined);
                    setImporting(true);
                    commitImport(householdId, file as File, {
                      accountId,
                      ...(profileId ? { profileId } : {}),
                      settings,
                      skipRows: [...skipped],
                      skipDuplicates,
                    })
                      .then((got) => {
                        setImported(got);
                        // The ledger, the balances and the dashboard all moved.
                        void client.invalidateQueries({
                          queryKey: ["households", householdId],
                        });
                      })
                      .catch(setImportError)
                      .finally(() => setImporting(false));
                  }}
                  className={`${buttonClass} mt-3 min-h-11`}
                  data-testid="commit-import"
                >
                  {importing
                    ? t("import.importing")
                    : imported
                      ? t("import.imported")
                      : t("import.importNow")}
                </button>
              </>
            )}

            <ErrorNote error={importError} />

            {imported && (
              // model.md §6.4: the effect is shown line by line, because a
              // number nobody can check is a number nobody can trust.
              <div className="mt-4" data-testid="import-result">
                <p className="text-sm text-ink">
                  {t("import.bookedOf", {
                    imported: imported.batch.rows_imported,
                    total: imported.batch.rows_total,
                  })}
                  {imported.batch.rows_skipped_duplicate > 0 &&
                    ` · ${t("import.duplicatesSkipped", {
                      n: imported.batch.rows_skipped_duplicate,
                    })}`}
                  {imported.batch.rows_skipped_by_user > 0 &&
                    ` · ${t("import.leftOut", { n: imported.batch.rows_skipped_by_user })}`}
                  {imported.batch.rows_failed > 0 &&
                    ` · ${t("import.failed", { n: imported.batch.rows_failed })}`}
                </p>
                <button
                  type="button"
                  disabled={importAction.isPending}
                  onClick={() =>
                    importAction.mutate(
                      { batchId: imported.batch.id, action: "revert" },
                      {
                        onSuccess: (got) =>
                          setImported({ batch: got.batch, rows: got.rows }),
                      },
                    )
                  }
                  className={`${secondaryButtonClass} mt-2 min-h-11`}
                  data-testid="undo-import"
                >
                  {t("import.undo")}
                </button>

                <ul className="mt-3 divide-y divide-line text-xs">
                  {imported.rows.map((row) => (
                    <li
                      key={row.row_number}
                      className="flex items-baseline justify-between gap-3 py-1.5"
                      data-testid="import-result-row"
                    >
                      <span className="text-muted">
                        {t("import.rowNumber", { n: row.row_number })}
                      </span>
                      <span className="text-ink">
                        {t(OUTCOME_LABELS[row.outcome])}
                        {row.error_message && ` — ${row.error_message}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Step>

          <Panel as="section" className="mt-6">
            <h2 className="text-sm font-semibold text-ink">{t("import.saveMapping")}</h2>
            <p className="mt-1 text-xs text-muted">{t("import.saveMappingHint")}</p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate({
                  ...(profileId ? { id: profileId } : {}),
                  settings: { ...settings, name },
                });
              }}
              noValidate
              className="mt-4"
            >
              <Field
                label={t("account.name")}
                htmlFor="import-name"
                error={violation("name")}
              >
                <input
                  id="import-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("import.profilePlaceholder")}
                  className={`${inputClass} min-h-11 text-base sm:text-sm`}
                />
              </Field>

              {accounts.data && accounts.data.data.length > 0 && (
                <Field label={t("import.defaultAccount")} htmlFor="import-account">
                  <select
                    id="import-account"
                    value={accountId}
                    onChange={(e) =>
                      setSettings((c) => ({
                        ...c,
                        default_account_id: e.target.value,
                      }))
                    }
                    className={`${inputClass} min-h-11 text-base sm:text-sm`}
                  >
                    <option value="">{t("import.noDefault")}</option>
                    {accounts.data.data.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <button
                type="submit"
                disabled={save.isPending}
                className={`${buttonClass} mt-4 min-h-11`}
                data-testid="save-profile"
              >
                {save.isPending ? t("common.saving") : t("import.saveProfile")}
              </button>
              {save.isSuccess && (
                <span className="ml-3 text-xs text-muted" data-testid="profile-saved">
                  {t("import.savedNote")}
                </span>
              )}
              <ErrorNote error={save.error} />
            </form>
          </Panel>
        </>
      )}

      {(imports.data?.items.length ?? 0) > 0 && !file && (
        <Section title={t("import.pastImports")} className="mt-10">
          <ul className={rowsClass}>
            {(imports.data?.items ?? []).map((batch) => (
              <li
                key={batch.id}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3 text-sm"
                data-testid="past-import"
              >
                <span className="min-w-0 truncate text-ink">{batch.source_filename}</span>
                <span className="flex shrink-0 items-center gap-3 text-xs text-muted">
                  <span>
                    {t("import.bookedOf", {
                      imported: batch.rows_imported,
                      total: batch.rows_total,
                    })}
                    {batch.reverted_at && ` · ${t("import.wasUndone")}`}
                  </span>
                  {/*
                    An import is reversible as a unit, and the undo is itself
                    reversible — so this is one button that changes its mind
                    rather than a destructive action with a confirmation.
                  */}
                  <button
                    type="button"
                    disabled={importAction.isPending}
                    onClick={() =>
                      importAction.mutate({
                        batchId: batch.id,
                        action: batch.reverted_at ? "restore" : "revert",
                      })
                    }
                    className={`${quietButtonClass} min-h-11`}
                    data-testid="past-import-action"
                  >
                    {batch.reverted_at ? t("import.restore") : t("import.revert")}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {saved.length > 0 && !file && (
        <Section title={t("import.savedProfiles")} className="mt-10">
          <ul className={rowsClass}>
            {saved.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-4 py-3 text-sm"
              >
                <span className="text-ink">{p.name}</span>
                <span className="text-xs text-muted">
                  {p.times_used === 0
                    ? t("import.neverUsed")
                    : t("import.timesUsed", { n: p.times_used })}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="mt-8">
        <button
          type="button"
          onClick={() => {
            setFile(undefined);
            setPreview(undefined);
            setSettings({});
          }}
          className={`${secondaryButtonClass} min-h-11`}
        >
          {t("import.reset")}
        </button>
      </div>
    </div>
  );
}

/**
 * One step of the file → mapping → preview → commit sequence, numbered because
 * it genuinely is one — unlike a page of independent sections, doing step 3
 * before step 1 is not an option here.
 */
function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <Panel as="section" className="mt-6">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-sm font-medium text-accent-text"
        >
          {n}
        </span>
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
      </div>
      <div className="mt-4">{children}</div>
    </Panel>
  );
}
